import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  type: ToastType;
  message: string;
  onClose: () => void;
  duration?: number;
  title?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  autoClose?: boolean;
}

// Explicitly define the icon components to avoid type issues
const ToastSuccess = CheckCircle;
const ToastError = XCircle;
const ToastInfo = AlertCircle;
const ToastWarning = AlertTriangle;

const icons = {
  success: ToastSuccess,
  error: ToastError,
  info: ToastInfo,
  warning: ToastWarning,
};

const styles = {
  base: 'flex flex-col max-w-md w-full rounded-lg border shadow-lg transform transition-all duration-300 ease-in-out',
  success: 'bg-green-50 text-green-800 border-green-200',
  error: 'bg-red-50 text-red-800 border-red-200',
  info: 'bg-blue-50 text-blue-800 border-blue-200',
  warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

const headerStyles = {
  base: 'flex items-center justify-between p-3 border-b',
  success: 'border-green-200 bg-green-100',
  error: 'border-red-200 bg-red-100',
  info: 'border-blue-200 bg-blue-100',
  warning: 'border-yellow-200 bg-yellow-100',
};

export function Toast({ 
  type, 
  message, 
  title, 
  onClose, 
  duration = 5000, 
  showRetry = false,
  onRetry,
  autoClose = true 
}: ToastProps) {
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (autoClose && duration > 0) {
      timer = setTimeout(() => {
        onClose();
      }, duration);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [duration, onClose, autoClose]);

  const Icon = icons[type];
  const displayTitle = title || type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div
      className={cn(styles.base, styles[type], 'animate-in slide-in-from-right-full')}
      role="alert"
    >
      <div className={cn(headerStyles.base, headerStyles[type])}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 flex-shrink-0" />
          <h4 className="font-semibold">{displayTitle}</h4>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-full p-1 hover:bg-black/10 transition-colors"
          aria-label="Close toast"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      <div className="p-3">
        <p className="text-sm">{message}</p>
        
        {showRetry && onRetry && (
          <button 
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium px-2 py-1 rounded hover:bg-black/5 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Try again
          </button>
        )}
      </div>
    </div>
  );
}